import { useEffect, useState } from 'react'
import { lockScroll, unlockScroll } from '../../lib/scrollLock'
import { usePropPick } from '../../hooks/useProps'
import { useAuth } from '../../hooks/useAuth'
import { useConnectionStatus } from '../../hooks/useConnections'
import { useCreateFlex } from '../../hooks/useHotTakes'
import LoadingSpinner from '../ui/LoadingSpinner'
import PickComments from '../social/PickComments'
import PropResultCard from './PropResultCard'
import PropLadderCard from './PropLadderCard'
import { toast } from '../ui/Toast'

/**
 * `groupPicks` — every pick this user made on the SAME player + market +
 * game. A slider prop invites a ladder, and opening one rung of eight to see
 * a single half-point in isolation tells you almost nothing. When it holds
 * more than one, the ladder card renders the whole set with its net, which is
 * the thing being asked about.
 *
 * Comments and the flex button stay bound to the tapped pick. They are per
 * pick server-side, and silently re-homing them onto a representative row
 * would move a user's comment to a pick they did not write it on.
 */
export default function PropDetailModal({ propPickId, groupPicks = null, onClose }) {
  const { data: pick, isLoading } = usePropPick(propPickId)
  const { session } = useAuth()
  const ownerId = pick?.user_id
  const isOwn = ownerId && ownerId === session?.user?.id
  const { data: connData } = useConnectionStatus(!isOwn ? ownerId : null)
  const canComment = isOwn || connData?.status === 'connected'
  const createFlex = useCreateFlex()
  const [flexing, setFlexing] = useState(false)
  const [flexText, setFlexText] = useState('')

  useEffect(() => {
    if (!propPickId) return
    lockScroll()
    return () => unlockScroll()
  }, [propPickId])

  if (!propPickId) return null

  async function handleSubmitFlex() {
    try {
      await createFlex.mutateAsync({ content: flexText, propPickId })
      toast('Flex posted to squad!', 'success')
      setFlexing(false)
      setFlexText('')
      onClose?.()
    } catch (err) {
      toast(err.message || 'Failed to flex', 'error')
    }
  }

  const canFlex = isOwn && pick?.status === 'settled' && pick?.is_correct === true

  const prop = pick?.player_props
  const game = prop?.games
  const isSettled = pick?.status === 'settled'
  const isLive = game?.status === 'in_progress' || game?.status === 'live'
  const borderColor = isSettled
    ? pick?.is_correct ? 'border-correct' : pick?.is_correct === false ? 'border-incorrect' : 'border-text-primary/20'
    : isLive ? 'border-accent' : 'border-text-primary/20'

  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center px-0 md:px-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/60" />
      <div
        className={`relative bg-bg-primary/90 backdrop-blur-md border ${borderColor} w-full md:max-w-md rounded-t-2xl md:rounded-2xl p-6 max-h-[85vh] overflow-y-auto`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Top bar: flex button + close */}
        <div className="flex items-center justify-between mb-3">
          {canFlex && !flexing ? (
            <button
              onClick={() => setFlexing(true)}
              className="flex items-center gap-1.5 text-sm font-semibold text-text-primary hover:opacity-80 transition-opacity"
            >
              <img src="/flex-button.png" alt="" className="w-6 h-6 object-contain" />
              <span>Flex to Squad</span>
            </button>
          ) : <div />}
          <button
            onClick={onClose}
            className="text-text-muted hover:text-text-primary text-xl leading-none w-8 h-8 flex items-center justify-center"
          >
            &times;
          </button>
        </div>

        {flexing && (
          <div className="mb-4">
            <textarea
              value={flexText}
              onChange={(e) => setFlexText(e.target.value)}
              placeholder="Let them know!"
              rows={2}
              className="w-full bg-bg-primary/50 border border-accent rounded-lg px-3 py-2 text-sm font-semibold text-white placeholder-text-muted focus:outline-none resize-none"
              autoFocus
            />
            <div className="flex gap-2 justify-end mt-2">
              <button onClick={() => { setFlexing(false); setFlexText('') }} className="text-xs text-text-muted hover:text-text-secondary px-3 py-1.5">Cancel</button>
              <button onClick={handleSubmitFlex} disabled={createFlex.isPending} className="text-xs font-semibold bg-accent text-white px-4 py-1.5 rounded-lg hover:bg-accent-hover disabled:opacity-50">
                {createFlex.isPending ? 'Posting...' : 'Flex'}
              </button>
            </div>
          </div>
        )}

        {isLoading ? (
          <LoadingSpinner />
        ) : !pick ? (
          <p className="text-text-muted text-center">Prop pick not found</p>
        ) : (
          <div className="space-y-4">
            {groupPicks?.length > 1
              ? <PropLadderCard picks={groupPicks} />
              : <PropResultCard pick={pick} />}

            {/* Comments */}
            <PickComments targetType="prop" targetId={propPickId} initialExpanded hideForm={!canComment} />
          </div>
        )}
      </div>
    </div>
  )
}
